import React, { useState, useEffect, useMemo, useRef } from 'react';
import 'bootstrap/dist/css/bootstrap.min.css'
import {sdk} from './sdk.js';
import {sdkLocal} from './sdkLocal.js';
import './AdDisplay.css';
import { SocialPostPageHeader } from "./SocialPostPageHeader";
import { PageLike } from "./PageLike";
import { HtmlAd } from "./HtmlAd";
import { MarketAdItem } from "./MarketAdItem";
import { useNavigate } from './Router';

const AdDisplay = ({ ad, zoneId = "peupleqc_wall", showPage = true, width, height, subid="" }) => {
  const navigate = useNavigate();
  const [impressionTracked, setImpressionTracked] = useState(false);
  const [errors, setErrors] = useState({});
  const adContainerRef = useRef(null);
  const [item, setItem] = useState(null);
  const [pageData, setPageData] = useState(null);
  const [user, setUser] = useState(null);
  const [loadingPage, setLoadingPage] = useState(false);
  const [loadingUser, setLoadingUser] = useState(false);

  // Récupérer l'utilisateur actuel
  useEffect(() => {
    const fetchUserData = async () => {
      try {
        setLoadingUser(true);
        const token = localStorage.getItem("liberium-token");
        if (token) {
          const mineStr = localStorage.getItem("myself");
          if (mineStr) {
            try {
              const mine = JSON.parse(mineStr);
              setUser(mine);
            } catch (parseError) {
              console.error("Erreur lors de la lecture des données utilisateur:", parseError);
              setErrors(prevErrors => ({
                ...prevErrors,
                user: "Erreur lors de la lecture des données utilisateur."
              }));
            }
          }
        }
      } catch (error) {
        console.error("Erreur lors de la récupération des données utilisateur:", error);
        setErrors(prevErrors => ({
          ...prevErrors,
          user: "Erreur lors de la récupération des données utilisateur."
        }));
      } finally {
        setLoadingUser(false);
      }
    };

    fetchUserData();
  }, []);

  // Traitement des market_item - TOUJOURS en dehors des conditions
  useEffect(() => {
    if (ad && ad.type === "market_item") {
      try {
        if (ad && ad.ad && ad.ad.items && ad.ad.items.length > 0) {
          const randomItem = ad.ad.items[Math.floor(Math.random() * ad.ad.items.length)];
          try {
            randomItem.medias = JSON.parse(randomItem.medias);
            setItem(randomItem);
          } catch (parseError) {
            console.error("Erreur lors du parsing des médias:", parseError);
            setErrors(prevErrors => ({
              ...prevErrors,
              item: "Erreur lors du traitement des médias de l'article."
            }));
          }
        }
      } catch (error) {
        console.error("Erreur lors du traitement de l'article:", error);
        setErrors(prevErrors => ({
          ...prevErrors,
          item: "Erreur lors du traitement de l'article."
        }));
      }
    }
  }, [ad]);

  // Récupérer les informations de la page si l'action est "Aimer la page"
  useEffect(() => {
    const fetchPageDetails = async () => {
      if (ad && ad.ad && ad.ad.action === "Aimer la page" && ad.ad.page_id) {
        try {
          setLoadingPage(true);
          const pageInfo = await sdk.models.SocialPages.getOne(ad.ad.page_id);
          if (pageInfo) {
            setPageData(pageInfo);
          } else {
            throw new Error("Aucune donnée de page reçue");
          }
        } catch (error) {
          console.error("Erreur lors de la récupération des détails de la page:", error);
          setErrors(prevErrors => ({
            ...prevErrors,
            page: "Erreur lors du chargement des informations de la page."
          }));
        } finally {
          setLoadingPage(false);
        }
      }
    };

    fetchPageDetails();
  }, [ad]);

  // Observer l'élément pour tracker les impressions - TOUJOURS avec gestion des ads 1x1
  useEffect(() => {
    if (!ad || !ad.ad) return;

    // Extraire les dimensions pour vérifier si c'est une ad 1x1
    const [med_width, med_height] = ad.ad.media ? 
      (ad.ad.media.size ? ad.ad.media.size.split('x').map(Number) : [width, height]) : 
      [width, height];
    
    const isPixelAd = med_width <= 1 && med_height <= 1;

    // Pour les ads 1x1, envoyer l'impression directement sans observer
    if (isPixelAd && !impressionTracked) {
      console.log("Tracking 1x1 pixel");
      trackImpression();
      return; // Sortir early, pas besoin d'observer
    }

    // Pour les autres ads, utiliser l'IntersectionObserver
    let observer;
    let isMounted = true;
    
    try {
      observer = new IntersectionObserver((entries) => {
        if (!isMounted) return;
        
        try {
          entries.forEach((entry) => {
            if (entry.isIntersecting && !impressionTracked) {
              trackImpression();
            }
          });
        } catch (error) {
          console.error("Erreur dans l'observer:", error);
        }
      }, { threshold: [0, 0.5] });

      if (adContainerRef.current) {
        observer.observe(adContainerRef.current);
      }
    } catch (error) {
      console.error("Erreur lors de la création de l'observer:", error);
      setErrors(prevErrors => ({
        ...prevErrors,
        observer: "Erreur lors de l'initialisation du suivi d'impression."
      }));
    }

    return () => {
      isMounted = false;
      try {
        if (observer && adContainerRef.current) {
          observer.unobserve(adContainerRef.current);
        }
        if (observer) {
          observer.disconnect();
        }
      } catch (error) {
        console.error("Erreur lors du nettoyage de l'observer:", error);
      }
    };
  }, [ad, impressionTracked, width, height]);

  // Vérification de validité de l'annonce
  if (!ad) {
    return <div className="text-danger">L'annonce est introuvable.</div>;
  }

  if (!ad.ad) {
    return <div className="text-danger">L'annonce est incorrecte.</div>;
  }

  // Traitement spécifique pour les annonces de type "market_item"
  if (ad.type === "market_item") {
    if (!item && !errors.item) {
      return <div className="text-muted">Chargement de l'article...</div>;
    }

    if (errors.item) {
      return <div className="text-danger">{errors.item}</div>;
    }

    return <MarketAdItem ad={ad.ad} item={item} zoneId={zoneId} subid={subid}/>;
  }

  const { id, action, actionValue, media, page_id } = ad.ad;
  
  // Extraire les dimensions du champ size pour tous les types de médias
  const [med_width, med_height] = media ? (media.size ? media.size.split('x') : [width, height]) : [width, height];

  // Fonction pour gérer les clics sur l'annonce
  const goClick = async () => {
    try {
      await trackClick();
      
      if (actionValue && actionValue.startsWith('https://peupleqc.com')) {
        const path = actionValue.replace('https://peupleqc.com', '');
        navigate(path || '/');
      } else if (action === "Aimer la page" && page_id) {
        navigate(`/page/${page_id}`);
      } else if (actionValue) {
        window.open(actionValue, '_blank');
      }
    } catch (error) {
      console.error("Erreur lors du clic sur l'annonce:", error);
      setErrors(prevErrors => ({
        ...prevErrors,
        click: "Erreur lors du traitement du clic."
      }));
    }
  };

  const onLikeChange = async () => {
    try {
      await sdk.models.AdsReaction.addReaction({
        adId: id,
        page_id: page_id,
        mediaId: null,
        zoneId: zoneId,
        isLikePage: 1
      });
    } catch (error) {
      console.error("Erreur lors de l'ajout de la réaction:", error);
      setErrors(prevErrors => ({
        ...prevErrors,
        reaction: "Erreur lors de l'ajout de la réaction."
      }));
    }
  };

  // Tracker les clics sur l'annonce
  const trackClick = async () => {
    try {
      if (action === "Aimer la page" && page_id) {
        console.log('passe icitte');
        console.log('page_id', page_id);
        await sdk.models.AdsClick.addClick({
          adId: id,
          likePageId: page_id,
          mediaId: null,
          zoneId: zoneId,
        });
      } else {
        await sdk.models.AdsClick.addClick({
          adId: id,
          mediaId: media ? media.id : null,
          zoneId: zoneId,
        });
      }
    } catch (error) {
      console.error("Erreur lors du suivi du clic:", error);
      setErrors(prevErrors => ({
        ...prevErrors,
        clickTracking: "Erreur lors du suivi du clic."
      }));
    }
  };

  // Tracker les impressions de l'annonce
  const trackImpression = async () => {
    if (!impressionTracked) {
      try {
        setImpressionTracked(true);
        await sdk.models.AdsImpression.addAdImpression({
          adId: id,
          mediaId: media ? media.id : null,
          zoneId: zoneId,
          subid: subid,
          refererUrl: window.location.href
        });
      } catch (error) {
        console.error("Erreur lors du suivi de l'impression:", error);
        setErrors(prevErrors => ({
          ...prevErrors,
          impression: "Erreur lors du suivi de l'impression."
        }));
        // Réinitialiser le flag pour permettre un nouveau tentative
        setImpressionTracked(false);
      }
    }
  };

  // Rendu spécial pour les annonces HTML
  if (media && media.htmlCode) {
    return (
      <div 
        ref={adContainerRef} 
        className="ad-container-display"
        style={{ 
          width: `${med_width}px`, 
          height: `${med_height}px`
        }}
      >
        <HtmlAd 
          ad={ad} 
          width={med_width+"px"}
          height={med_height+"px"}
        />
      </div>
    );
  }

  // Rendu pour l'action "Aimer la page"
  if (action === "Aimer la page") {
    return (
      <div ref={adContainerRef} className="ad-container-display ad-page-like-container-display">
        <div className="ad-card-display ad-page-card-display">
          {showPage && (
            <div className="ad-card-header-display">
              <SocialPostPageHeader
                pageId={page_id}
                underMessage={<span className="text-muted">Publicité</span>}
              />
            </div>
          )}
          
          {loadingPage && (
            <div className="text-center p-3">
              <div className="text-muted">Chargement des informations de la page...</div>
            </div>
          )}
          
          {errors.page && (
            <div className="text-danger text-center p-3">
              {errors.page}
            </div>
          )}
          
          {pageData && !loadingPage && (
            <>
              <div className="ad-page-cover-wrapper-display" onClick={goClick}>
                {pageData.cover ? (
                  <img
                    src={`https://peupleqc.com/s/peupleqc-upload/${pageData.cover}`}
                    alt="Couverture de la page"
                    className="ad-page-cover-display"
                    onError={(e) => {
                      console.error("Erreur lors du chargement de l'image de couverture");
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'block';
                    }}
                  />
                ) : (
                  <div className="ad-page-cover-placeholder-display">
                    <span>{pageData.name}</span>
                  </div>
                )}
              </div>
              
              <div className="ad-page-info-display">
                <div className="ad-page-description-display">
                  {pageData.description || "Rejoignez cette page pour plus d'informations"}
                </div>
                
                <div className="ad-page-like-wrapper-display">
                  {user && !loadingUser && (
                    <PageLike pageId={page_id} userId={user.user_id} onLikeChange={onLikeChange}/>
                  )}
                  {loadingUser && (
                    <div className="text-muted">Chargement...</div>
                  )}
                </div>
              </div>
            </>
          )}
          
          <div className="ad-card-footer-display ad-page-footer-display">
            <div
              className="ad-page-visit-btn-display btn btn-primary"
              onClick={goClick}
              style={{ cursor: 'pointer' }}
            >
              Visiter la page
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Rendu standard pour les autres types d'annonces
  return (
    <div ref={adContainerRef} className="ad-container-display">
      <div className="ad-card-display">
        {showPage && (
          <div className="ad-card-header-display">
            <SocialPostPageHeader
              pageId={page_id}
              underMessage={<span className="text-muted">Publicité</span>}
            />
          </div>
        )}
        <div className="ad-media-wrapper-display" onClick={goClick}>
          {media && media.media_url ? (
            <img
              src={media.media_url}
              alt="Ad Media"
              className="ad-media-img-display"
              style={{ 
                width: `${med_width}px`, 
                height: `${med_height}px`
              }}
              onError={(e) => {
                console.error("Erreur lors du chargement de l'image publicitaire");
                e.target.style.display = 'none';
                e.target.nextSibling.style.display = 'block';
              }}
            />
          ) : (
            <div className="ad-media-placeholder-display">
              Contenu publicitaire
            </div>
          )}
        </div>
        {showPage && action === "Visiter une page" && (
          <div className="ad-card-footer-display">
            <div
              className="ad-action-btn-display btn btn-primary"
              onClick={goClick}
              style={{ cursor: 'pointer' }}
            >
              Visiter la page
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export { AdDisplay };